import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, Navigate } from 'react-router';

import { CurrentUserResponse, IAuthContext, StartSessionResponse } from '../../types/auth';
import { closeSession, failure, startSession } from '../../types/api';

import apiFunctions from '../../api';

export const useAuth = () => {
  return React.useContext(AuthContext);
};

export const AuthContext = React.createContext<IAuthContext>({
  user: undefined,
  sessionToken: undefined,
  roles: [],
  isLoadingRoles: false,
  hasRole: () => false,
  login: () => Promise.resolve(),
  logout: () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<string | undefined>(sessionStorage.getItem('User') ?? undefined);
  const [sessionToken, setSessionToken] = useState<string | undefined>(
    sessionStorage.getItem('SessionToken') ?? undefined,
  );
  const [roles, setRoles] = useState<string[]>([]);
  // A restored session starts with unresolved roles, so role-gated surfaces can wait instead of
  // rendering a denial that a resolved Admin would immediately replace.
  const [isLoadingRoles, setIsLoadingRoles] = useState(sessionStorage.getItem('SessionToken') != null);

  const loadCurrentUser = useCallback(async () => {
    const currentUser = await apiFunctions.apiGet<CurrentUserResponse>('Auth/me');
    setUser(currentUser.email);
    setRoles(currentUser.roles ?? []);
    sessionStorage.setItem('User', currentUser.email);
    return currentUser;
  }, []);

  useEffect(() => {
    if (!sessionToken) {
      setIsLoadingRoles(false);
      return;
    }

    setIsLoadingRoles(true);
    void loadCurrentUser()
      .catch((error) => {
        console.error('Could not refresh the current user.', error);
      })
      .finally(() => setIsLoadingRoles(false));
  }, [loadCurrentUser, sessionToken]);

  const login = async (code: string) => {
    const response = (await apiFunctions.apiPost(startSession, { linkCode: code })) as StartSessionResponse;
    if (response.userEmail == null || response.sessionToken == null) return failure;

    setSessionToken(response.sessionToken);
    sessionStorage.setItem('SessionToken', response.sessionToken);
    setUser(response.userEmail);
    sessionStorage.setItem('User', response.userEmail);

    return response;
  };

  const logout = async (callback: VoidFunction) => {
    await apiFunctions.apiPost(closeSession).then(() => {
      setUser(undefined);
      setSessionToken(undefined);
      setRoles([]);
      sessionStorage.removeItem('SessionToken');
      sessionStorage.removeItem('User');
      callback();
    });
  };

  const hasRole = (role: string) => roles.includes(role);
  const value = { user, sessionToken, roles, isLoadingRoles, hasRole, login, logout };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const Authorized = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuth();
  const location = useLocation();

  if (!auth.user) return <Navigate to="/login" state={{ from: location }}></Navigate>;
  else return <>{children}</>;
};
